import React, { useState, useEffect } from 'react';
import { CheckCircle, ChevronRight, ChevronLeft, Loader2, Shield, Briefcase, Clock, FileText, CreditCard } from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import axios from 'axios';

const API = '/api';

const CASE_CATEGORIES = [
  'Arbeidsrett',
  'Familie & Arv',
  'Kontrakt',
  'Eiendom',
  'Forbruker',
  'Selskapsrett',
  'Skatt',
  'Erstatning'
];

const COMPETENCE_LEVELS = [
  { value: 'BEGINNER', label: 'Nybegynner', desc: 'Begrenset erfaring' },
  { value: 'INTERMEDIATE', label: 'God kunnskap', desc: '1-3 års erfaring' },
  { value: 'EXPERT', label: 'Ekspert', desc: '3+ års erfaring' }
];

const AFAOnboardingWizard = ({ onComplete }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    // Step 1: Identitet
    bevillingsnummer: '',
    jurisdiksjon: 'Norge',
    
    // Step 2: Kompetanse
    fagomraader: [],
    kompetansenivaa: {},
    bio: '',
    
    // Step 3: Kapasitet
    afa_cases_per_week: 5,
    sla_hours: 48,
    
    // Step 4: Terms
    terms_accepted: false,
    
    // Step 5: Payout
    payout_info: {
      account_number: '',
      tax_id: ''
    }
  });

  const totalSteps = 5;

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const updateNestedField = (parent, field, value) => {
    setFormData(prev => ({
      ...prev,
      [parent]: { ...prev[parent], [field]: value }
    }));
  };

  const saveDraft = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/afa/profile`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (error) {
      console.error('Save draft error:', error);
    }
  };

  const handleNext = async () => {
    // Validate current step
    if (currentStep === 1) {
      if (!formData.bevillingsnummer) {
        toast.error('Vennligst fyll inn bevillingsnummer');
        return;
      }
    } else if (currentStep === 2) {
      if (formData.fagomraader.length === 0) {
        toast.error('Velg minst ett fagområde');
        return;
      }
    }

    // Save draft
    await saveDraft();

    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    if (!formData.terms_accepted) {
      toast.error('Du må akseptere vilkårene for å fortsette');
      return;
    }

    if (!formData.payout_info.account_number) {
      toast.error('Vennligst fyll inn kontonummer');
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      
      // Save final data
      await axios.put(`${API}/afa/profile`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Submit for approval
      const response = await axios.post(`${API}/afa/profile/submit`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('🎉 AFA-profil godkjent!');
      
      if (onComplete) {
        onComplete(response.data);
      }
    } catch (error) {
      console.error('Submit error:', error);
      toast.error(error.response?.data?.detail || 'Kunne ikke sende inn profil');
    } finally {
      setLoading(false);
    }
  };

  const toggleFagomraade = (fagomraade) => {
    const current = formData.fagomraader;
    if (current.includes(fagomraade)) {
      updateField('fagomraader', current.filter(f => f !== fagomraade));
      // Remove from kompetansenivaa as well
      const newKompetanse = { ...formData.kompetansenivaa };
      delete newKompetanse[fagomraade];
      updateField('kompetansenivaa', newKompetanse);
    } else {
      updateField('fagomraader', [...current, fagomraade]);
      // Set default level
      updateField('kompetansenivaa', {
        ...formData.kompetansenivaa,
        [fagomraade]: 'INTERMEDIATE'
      });
    }
  };

  const setCompetenceLevel = (fagomraade, level) => {
    updateField('kompetansenivaa', {
      ...formData.kompetansenivaa,
      [fagomraade]: level
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Bli AFA-advokat
          </h1>
          <p className="text-gray-600">
            Fullfør profilen din for å få tilgang til Advokat for alle-saker
          </p>
        </div>

        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">
              Steg {currentStep} av {totalSteps}
            </span>
            <span className="text-sm text-gray-500">
              {Math.round((currentStep / totalSteps) * 100)}%
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-indigo-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${(currentStep / totalSteps) * 100}%` }}
            />
          </div>
        </div>

        {/* Form Card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
          {/* Step 1: Identitet */}
          {currentStep === 1 && (
            <div className="space-y-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center">
                  <Shield className="w-6 h-6 text-indigo-600" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">Identitet & Bevilling</h2>
                  <p className="text-sm text-gray-600">Verifiser din juridiske autorisasjon</p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Bevillingsnummer *
                </label>
                <input
                  type="text"
                  value={formData.bevillingsnummer}
                  onChange={(e) => updateField('bevillingsnummer', e.target.value)}
                  placeholder="123456"
                  className="w-full border border-gray-300 rounded-md px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-gray-400"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  Ditt bevillingsnummer fra Tilsynsrådet for advokatvirksomhet
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Jurisdiksjon *
                </label>
                <select
                  value={formData.jurisdiksjon}
                  onChange={(e) => updateField('jurisdiksjon', e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-gray-400"
                >
                  <option value="Norge">Norge</option>
                  <option value="Sverige">Sverige</option>
                  <option value="Danmark">Danmark</option>
                </select>
              </div>
            </div>
          )}

          {/* Step 2: Kompetanse */}
          {currentStep === 2 && (
            <div className="space-y-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center">
                  <Briefcase className="w-6 h-6 text-indigo-600" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">Fagområder & Kompetanse</h2>
                  <p className="text-sm text-gray-600">Velg dine spesialområder</p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Fagområder * (Velg minst ett)
                </label>
                <div className="grid grid-cols-2 gap-3">
                  {CASE_CATEGORIES.map(category => (
                    <button
                      key={category}
                      onClick={() => toggleFagomraade(category)}
                      className={`p-3 rounded-lg border-2 text-left transition-all ${
                        formData.fagomraader.includes(category)
                          ? 'border-indigo-600 bg-gray-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-gray-900">{category}</span>
                        {formData.fagomraader.includes(category) && (
                          <CheckCircle className="w-5 h-5 text-indigo-600" />
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Competence Levels */}
              {formData.fagomraader.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Kompetansenivå
                  </label>
                  <div className="space-y-3">
                    {formData.fagomraader.map(fagomraade => (
                      <div key={fagomraade} className="p-4 bg-gray-50 rounded-lg">
                        <p className="text-sm font-medium text-gray-900 mb-2">{fagomraade}</p>
                        <div className="grid grid-cols-3 gap-2">
                          {COMPETENCE_LEVELS.map(level => (
                            <button
                              key={level.value}
                              onClick={() => setCompetenceLevel(fagomraade, level.value)}
                              className={`p-2 rounded border-2 text-xs text-center transition-all ${
                                formData.kompetansenivaa[fagomraade] === level.value
                                  ? 'border-indigo-600 bg-gray-50 font-semibold text-indigo-900'
                                  : 'border-gray-200 hover:border-gray-300'
                              }`}
                            >
                              {level.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Kort bio (valgfri)
                </label>
                <textarea
                  value={formData.bio}
                  onChange={(e) => updateField('bio', e.target.value)}
                  placeholder="F.eks: Spesialist i arbeidsrett med 5 års erfaring..."
                  rows={3}
                  className="w-full border border-gray-300 rounded-md px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-gray-400 resize-none"
                />
              </div>
            </div>
          )}

          {/* Step 3: Kapasitet */}
          {currentStep === 3 && (
            <div className="space-y-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center">
                  <Clock className="w-6 h-6 text-indigo-600" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">Kapasitet & Responstid</h2>
                  <p className="text-sm text-gray-600">Sett dine forventninger</p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  AFA-saker per uke *
                </label>
                <select
                  value={formData.afa_cases_per_week}
                  onChange={(e) => updateField('afa_cases_per_week', parseInt(e.target.value))}
                  className="w-full border border-gray-300 rounded-md px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-gray-400"
                >
                  <option value={3}>3 saker/uke (Deltid)</option>
                  <option value={5}>5 saker/uke (Standard)</option>
                  <option value={10}>10 saker/uke (Fulltid)</option>
                  <option value={15}>15+ saker/uke (Høy kapasitet)</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Ønsket SLA (Service Level Agreement) *
                </label>
                <div className="space-y-2">
                  {[
                    { value: 24, label: '24 timer', desc: 'Ekstra rask levering' },
                    { value: 48, label: '48 timer', desc: 'Standard levering' },
                    { value: 72, label: '72 timer', desc: 'Fleksibel levering' }
                  ].map(option => (
                    <button
                      key={option.value}
                      onClick={() => updateField('sla_hours', option.value)}
                      className={`w-full p-4 rounded-lg border-2 text-left transition-all ${
                        formData.sla_hours === option.value
                          ? 'border-indigo-600 bg-gray-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-semibold text-gray-900">{option.label}</p>
                          <p className="text-sm text-gray-600">{option.desc}</p>
                        </div>
                        {formData.sla_hours === option.value && (
                          <CheckCircle className="w-5 h-5 text-indigo-600" />
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Step 4: Terms */}
          {currentStep === 4 && (
            <div className="space-y-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center">
                  <FileText className="w-6 h-6 text-indigo-600" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">Leveransestandard & Vilkår</h2>
                  <p className="text-sm text-gray-600">Forstå AFA-modellen</p>
                </div>
              </div>

              <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 space-y-4">
                <h3 className="font-semibold text-gray-900">Slik fungerer AFA-pakker:</h3>
                
                <div className="space-y-3 text-sm text-gray-700">
                  <div className="flex gap-2">
                    <span className="font-medium">📦</span>
                    <p><strong>Fastpris-pakker:</strong> Basis (1500 kr), Dokument (2500 kr), Utvidet (4500 kr)</p>
                  </div>
                  
                  <div className="flex gap-2">
                    <span className="font-medium">📝</span>
                    <p><strong>Scope:</strong> Du leverer innenfor pakke-rammene. Basis = kun vurdering, Dokument = 1 dok, Utvidet = 5 dok.</p>
                  </div>
                  
                  <div className="flex gap-2">
                    <span className="font-medium">💰</span>
                    <p><strong>Utbetaling:</strong> 80% til deg, 20% til Lexy. Utbetales etter levering.</p>
                  </div>
                  
                  <div className="flex gap-2">
                    <span className="font-medium">⏰</span>
                    <p><strong>SLA:</strong> Du må levere innen din valgte SLA. Bruker får refusjon hvis du bryter SLA.</p>
                  </div>
                  
                  <div className="flex gap-2">
                    <span className="font-medium">⚡</span>
                    <p><strong>Eskalering:</strong> Hvis saken er mer kompleks, kan du sende tilbud til kunden om oppgradering.</p>
                  </div>
                </div>
              </div>

              <div className="border border-gray-200 rounded-lg p-4">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.terms_accepted}
                    onChange={(e) => updateField('terms_accepted', e.target.checked)}
                    className="mt-1"
                  />
                  <span className="text-sm text-gray-700">
                    Jeg forstår og aksepterer AFA-modellen, inkludert fastpris-struktur, scope-begrensninger og SLA-forpliktelser. *
                  </span>
                </label>
              </div>
            </div>
          )}

          {/* Step 5: Payout */}
          {currentStep === 5 && (
            <div className="space-y-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center">
                  <CreditCard className="w-6 h-6 text-indigo-600" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">Utbetaling</h2>
                  <p className="text-sm text-gray-600">Hvor skal pengene gå?</p>
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-blue-900">
                  💡 <strong>80/20-modellen:</strong> Du får 80% av pakkeprisen utbetalt etter levering. Lexy tar 20% for plattform, matching og support.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Kontonummer *
                </label>
                <input
                  type="text"
                  value={formData.payout_info.account_number}
                  onChange={(e) => updateNestedField('payout_info', 'account_number', e.target.value)}
                  placeholder="1234 56 78901"
                  className="w-full border border-gray-300 rounded-md px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-gray-400"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Organisasjonsnummer / Fødselsnummer *
                </label>
                <input
                  type="text"
                  value={formData.payout_info.tax_id}
                  onChange={(e) => updateNestedField('payout_info', 'tax_id', e.target.value)}
                  placeholder="123456789"
                  className="w-full border border-gray-300 rounded-md px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-gray-400"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  For skattemessige formål
                </p>
              </div>
            </div>
          )}

          {/* Navigation */}
          <div className="flex gap-3 mt-8 pt-6 border-t border-gray-200">
            {currentStep > 1 && (
              <Button
                onClick={handleBack}
                variant="outline"
                className="flex-1"
              >
                <ChevronLeft className="w-4 h-4 mr-2" />
                Tilbake
              </Button>
            )}
            
            {currentStep < totalSteps ? (
              <Button
                onClick={handleNext}
                className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white"
              >
                Neste
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <Button
                onClick={handleSubmit}
                disabled={loading}
                className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Sender inn...
                  </>
                ) : (
                  'Send inn profil'
                )}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AFAOnboardingWizard;
